/**
 * balance-structures.ts — thin helper surface over `state.structuresData`.
 *
 * v3M14b migrated every per-kind tunable out of file-level constants and
 * into `src/data/structures.json` (loaded into `StructuresData` by
 * `loader.ts`). All helpers below take the loaded catalogue as the first
 * argument so the runtime — not the bundle — is the single source of
 * truth. The xlsx → json pipeline is the place to retune balance from
 * v3M14c onwards; do not reintroduce hard-coded numerics here.
 *
 * Exception: `maxStructuresForSize` stays pure-of-catalogue because it's
 * a function of `PlanetSize` per PLANETS §2.1 (slots = size), not of the
 * structures catalogue.
 */

import type {
  PlanetSize,
  ResourceKind,
  StructureKind,
  StructuresData,
} from '../state/types/index';

/**
 * Max structure slots per planet (PLANETS §2.1 — "maxStructures = Size × 1").
 * MFix1.4: PlanetSize is a number in [5, 20] so slots = size directly.
 * Returns the integer cap; callers compare `planet.structureIds.length`
 * against this. Independent of `StructuresData` — this is a planet-size
 * rule, not a per-kind tunable.
 */
export function maxStructuresForSize(size: PlanetSize): number {
  return size;
}

// ---------------------------------------------------------------------------
// Per-kind tunables
// ---------------------------------------------------------------------------

/**
 * Credits cost for an upgrade step (level N → N+1) for the given kind.
 * Used by `commands.ts` (build + upgrade) and `advance-ai.ts` (AI
 * affordability checks). Returns 0 if the catalogue lacks the kind —
 * which would indicate a missing row, but defensively the structure
 * appears free rather than crashing the sim.
 */
export function buildCreditsForKind(
  structures: StructuresData,
  kind: StructureKind,
): number {
  return structures.byKind[kind]?.buildCreditsPerLevel ?? 0;
}

/** Minerals cost for an upgrade step (level N → N+1) for the given kind. */
export function buildMineralsForKind(
  structures: StructuresData,
  kind: StructureKind,
): number {
  return structures.byKind[kind]?.buildMineralsPerLevel ?? 0;
}

/**
 * Ticks to build/upgrade TO `level` (1-indexed). `buildTicksByLevel[0]`
 * is the time to reach L1 from a fresh slot; `buildTicksByLevel[N-1]`
 * is N → N+1's countdown. Returns 0 for out-of-range levels (caller
 * should already have validated against `maxLevelForKind`).
 */
export function buildTicksForLevel(
  structures: StructuresData,
  kind: StructureKind,
  level: number,
): number {
  if (level < 1) return 0;
  const row = structures.byKind[kind];
  if (!row) return 0;
  return row.buildTicksByLevel[level - 1] ?? 0;
}

/** Max hull at the given level for the given kind. 1-indexed level. */
export function maxHullForLevel(
  structures: StructuresData,
  kind: StructureKind,
  level: number,
): number {
  if (level < 1) return 0;
  const row = structures.byKind[kind];
  if (!row) return 0;
  return row.hullByLevel[level - 1] ?? 0;
}

/**
 * Per-tick credits upkeep for an active (level >= 1) structure of the
 * given kind. Economy boosters (Cloning Center / Trade Center / Processing
 * Plant / Research Lab / Refinery) authored as upkeepCredits = 0 in the
 * json so this falls out of the catalogue without a dispatch table.
 */
export function upkeepForKind(
  structures: StructuresData,
  kind: StructureKind,
): number {
  return structures.byKind[kind]?.upkeepCredits ?? 0;
}

/**
 * Highest level a given structure kind can be upgraded to (race-agnostic).
 * Falls back to 6 if the catalogue lacks the row — defensive default
 * matching the historical `DEFAULT_MAX_STRUCTURE_LEVEL`.
 */
export function maxLevelForKind(
  structures: StructuresData,
  kind: StructureKind,
): number {
  return structures.byKind[kind]?.maxLevel ?? 6;
}

/**
 * Highest level a given structure kind can be upgraded to for a specific
 * race. Falls back to `maxLevelForKind` when no race override exists.
 * MFix5.3 introduced race-gating for Embassy (RACE-AA L2, BB/CC L1);
 * future race-locked structures use the same lookup.
 */
export function maxLevelForKindByRace(
  structures: StructuresData,
  kind: StructureKind,
  raceId: string,
): number {
  const row = structures.byKind[kind];
  if (!row) return 6;
  const override = row.maxLevelByRace[raceId];
  if (override !== undefined) return override;
  return row.maxLevel;
}

/**
 * True iff the given kind is unique-per-planet (one instance max).
 * Replaces the prior `UNIQUE_STRUCTURE_KINDS` set — the `unique` flag
 * lives on the catalogue row.
 */
export function isUniqueKind(
  structures: StructuresData,
  kind: StructureKind,
): boolean {
  return structures.byKind[kind]?.unique === true;
}

/**
 * Economy-output multiplier for a planet's per-tick resource generation
 * from an active structure of the given kind at the given level.
 *
 * level === 0 (under construction) returns 1.0 — a structure not yet
 * commissioned doesn't multiply anything. Active boosters return their
 * per-level entry from the catalogue (1.25 at L1, +0.25/level, 2.5 at L6
 * with the v1 defaults). Non-boosters return 1.0 (no `multiplier`
 * effect declared).
 *
 * v3M27 — reads the row's first `multiplier` effect rather than the
 * old `economyMultiplierByLevel` array. Booster kinds declare exactly
 * one such effect so this is unambiguous.
 */
export function economyMultiplierForLevel(
  structures: StructuresData,
  kind: StructureKind,
  level: number,
): number {
  if (level < 1) return 1;
  const row = structures.byKind[kind];
  if (!row) return 1;
  for (const effect of row.effects) {
    if (effect.kind === 'multiplier') {
      return effect.perLevel[level - 1] ?? 1;
    }
  }
  return 1;
}

// ---------------------------------------------------------------------------
// Global tunables
// ---------------------------------------------------------------------------

/**
 * Auto-repair proportion of max hull per tick (STRUCTURES §4 — 2% of max
 * Hull per tick, automatic, no external facility required).
 */
export function autoRepairProportion(structures: StructuresData): number {
  return structures.autoRepairProportionPerTick;
}

/**
 * Baseline empire resource cap with no storage structures, by resource
 * kind. Research is uncapped (flows into research project per
 * ECONOMY §4.4); diplomatic uses its own hard cap; population caps come
 * from planet sums. Defaults to 0 for any other kind.
 *
 * v3M27 — `storageCapPerLevel` removed. Per-level cap bonuses now live
 * inside each storage row's `capBonus` effect; consumers go through
 * `capBonusForEmpire` in `balance-structure-effects.ts` instead.
 */
export function baseCapFor(
  structures: StructuresData,
  resource: ResourceKind,
): number {
  switch (resource) {
    case 'credits':
      return structures.baseCredits;
    case 'minerals':
      return structures.baseMinerals;
    case 'fuel':
      return structures.baseFuel;
    default:
      return 0;
  }
}

/**
 * Hard cap on Diplomatic Influence (STRUCTURES §2.3 / ECONOMY §2.6).
 * Total across the empire, regardless of Embassy count.
 */
export function diplomaticCap(structures: StructuresData): number {
  return structures.diplomaticCap;
}

/**
 * Diplomatic Influence generated per Embassy per tick at the given
 * level (1-indexed). Returns 0 for level 0 (under construction) and
 * for out-of-range levels.
 *
 * v3M27 — reads the embassy row's `perTickIncome` effect targeting
 * the `'diplomatic'` resource. The legacy top-level
 * `embassyDiplomacyByLevel` field has been removed.
 */
export function embassyDiplomacyAtLevel(
  structures: StructuresData,
  level: number,
): number {
  if (level < 1) return 0;
  const row = structures.byKind['embassy'];
  if (!row) return 0;
  for (const effect of row.effects) {
    if (effect.kind === 'perTickIncome' && effect.resource === 'diplomatic') {
      return effect.perLevel[level - 1] ?? 0;
    }
  }
  return 0;
}
